import { maskVendor } from "@/shared/lib/vendorMask";
import { errorToast } from "@/shared/lib/apiErrors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Calendar, X, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { posterService } from "../../../api/posterService";
import { toast } from "@/shared/hooks/use-toast";

/**
 * S3 (2026-05-07) 海报定时出图 — 工作台「已排程」面板。
 *
 * <p>列出当前租户 PENDING / FIRED / FAILED 的 schedule;PENDING 可取消,FIRED 显示已转 task,
 * FAILED 显示失败原因。空时整段不渲染,不占顶部位置。
 */
export function ScheduleListSection() {
  const { t } = useTranslation("poster");
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["poster-schedules"],
    queryFn: posterService.listSchedules,
    refetchInterval: 60_000,
  });

  const items = q.data ?? [];
  if (q.isLoading) {
    return (
      <Card className="mb-2">
        <CardContent className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> {t("schedule.loading")}
        </CardContent>
      </Card>
    );
  }
  if (items.length === 0) return null;

  async function cancel(id: number) {
    try {
      await posterService.cancelSchedule(id);
      toast({ title: t("toast.scheduleCanceled") });
      qc.invalidateQueries({ queryKey: ["poster-schedules"] });
    } catch (e: unknown) {
      toast(errorToast(e, { fallbackTitle: t("toast.cancelFailed") }));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          {t("schedule.title")} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((s, i) => (
          <div
            key={s.scheduleId}
            className="flex items-start justify-between rounded-md border p-3 fx-enter fx-hover-lift"
            style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
          >
            <div className="space-y-1">
              <div className="text-sm font-medium">{s.taskName}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                {s.scheduledAt.replace("T", " ").slice(0, 16)}
                <span className="text-muted-foreground/60">·</span>
                <StatusBadge status={s.status} />
              </div>
              {s.status === "FAILED" && s.failureReason && (
                <div className="flex items-start gap-1 text-xs text-warning">
                  <AlertTriangle className="mt-0.5 h-3 w-3" />
                  {maskVendor(s.failureReason)}
                </div>
              )}
              {s.status === "FIRED" && s.taskId && (
                <a
                  href={`/ai-haibao/tasks/${s.taskId}`}
                  className="text-xs text-primary hover:underline"
                >
                  {t("schedule.viewTask", { id: s.taskId })}
                </a>
              )}
            </div>
            {s.status === "PENDING" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancel(s.scheduleId)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3 mr-1" /> {t("schedule.cancel")}
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    PENDING: { label: "等待中", cls: "text-warning" },
    FIRED: { label: "已触发", cls: "text-success" },
    CANCELED: { label: "已取消", cls: "text-muted-foreground" },
    FAILED: { label: "失败", cls: "text-destructive" },
  };
  const m = map[status] ?? { label: status, cls: "text-muted-foreground" };
  return <span className={m.cls}>{m.label}</span>;
}
